import apiClient from './apiClient';
import { loadDicomFile } from './dicomLoader';
import { concurrencyPool, downloadWithRetry } from '../utils/concurrencyPool';

// Configuration
const DOWNLOAD_CONCURRENCY = 8; // Number of simultaneous download+parse operations

/**
 * Download Service - Downloads DICOM files from backend and caches in IndexedDB
 */
const downloadService = {
  /**
   * Download all DICOM files for a study from backend with parallel downloads
   * GET /api/v1/studies/{study_id}/download-urls
   * @param {string} studyId - Backend study ID
   * @param {Function} onProgress - Progress callback receiving (percentage, completed, total)
   * @param {AbortSignal} signal - Optional AbortSignal for cancellation
   */
  async downloadStudy(studyId, onProgress = null, signal = null) {
    try {
      // Step 1: Get download URLs from backend
      const response = await apiClient.get(`/studies/${studyId}/download-urls`);
      
      const { study_instance_uid, series } = response.data;
      
      if (!series || series.length === 0) {
        throw new Error('No series found in study');
      }

      // Step 2: Flatten all images into a single array for parallel processing
      const allImages = [];
      series.forEach(seriesData => {
        const images = seriesData.images || [];
        images.forEach(imageData => {
          allImages.push({
            url: imageData.download_url,
            filename: imageData.filename || 'image.dcm',
            seriesInstanceUID: seriesData.series_instance_uid,
          });
        });
      });

      const totalImages = allImages.length;
      
      if (totalImages === 0) {
        throw new Error('No images found in study');
      }

      console.log(`Starting parallel download of ${totalImages} images with concurrency ${DOWNLOAD_CONCURRENCY}`);

      // Step 3: Download and parse each file in parallel
      // This combines download + parse into a single operation per file
      // Memory efficient: blob is released immediately after parsing
      const result = await concurrencyPool(
        allImages,
        async (imageData, index) => {
          // Download file with retry logic
          const blob = await downloadWithRetry(imageData.url, {
            signal,
            retries: 2,
            retryDelay: 1000,
          });

          // Create File object
          const file = new File([blob], imageData.filename, {
            type: 'application/dicom'
          });

          // Parse DICOM and store in IndexedDB
          // After this, the blob is eligible for garbage collection
          await loadDicomFile(file);

          return {
            filename: imageData.filename,
            success: true,
          };
        },
        {
          concurrency: DOWNLOAD_CONCURRENCY,
          signal,
          onProgress: ({ completed, total }) => {
            if (onProgress) {
              const progress = Math.round((completed / total) * 100);
              // Pass percentage, completed, and total to callback
              onProgress(progress, completed, total);
            }
          },
          retries: 2, // Retry entire download+parse operation
          retryDelay: 1000,
        }
      );

      // Check for errors
      if (result.errors.length > 0) {
        console.warn(`${result.errors.length} images failed to download/parse:`);
        result.errors.forEach(err => {
          console.error(`  - ${err.item.filename}:`, err.error.message);
        });
      }

      const successCount = result.results.filter(r => r?.success).length;

      if (successCount === 0) {
        throw new Error('Failed to download and process any DICOM files');
      }

      console.log(`Successfully processed ${successCount}/${totalImages} images`);

      // Finalize series orientations (LOCALIZER detection)
      // Process each series individually
      const { finalizeSeriesOrientation } = await import('./dicomLoader');
      
      for (const seriesData of series) {
        if (seriesData.series_instance_uid) {
          await finalizeSeriesOrientation(seriesData.series_instance_uid);
        }
      }

      // Final progress callback
      if (onProgress) {
        onProgress(100, successCount, totalImages);
      }

      // Final progress callback
      if (onProgress) {
        onProgress(100, successCount, totalImages);
      }

      return {
        success: true,
        studyInstanceUID: study_instance_uid,
        processedCount: successCount,
        totalImages,
        failedCount: result.errors.length,
      };

    } catch (error) {
      // Handle cancellation
      if (signal?.aborted || error.message === 'Operation cancelled') {
        console.log('Download cancelled by user');
        return {
          success: false,
          error: 'Download cancelled',
          cancelled: true,
        };
      }

      // Handle other errors
      let errorMessage = 'Failed to download study';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view this study.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Study not found.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error('Download study error:', error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  },

  /**
   * Download a single file from presigned URL (legacy method, kept for compatibility)
   */
  async downloadFile(url) {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }
    
    return await response.blob();
  },

  /**
   * Check if a study is already cached in IndexedDB
   */
  async isStudyCached(studyInstanceUID) {
    try {
      const db = (await import('../database/db')).default;
      const studies = await db.studies.where('studyInstanceUID').equals(studyInstanceUID).toArray();
      return studies.length > 0;
    } catch (error) {
      return false;
    }
  },
};

export default downloadService;